import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { CacheStatus, ModelRef } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useModelPicker } from "../ai/ModelPicker.js";
import { formatNumber } from "../lib/format.js";

interface Props {
  projectId: string;
  status: CacheStatus | null;
  building: boolean;
  progress: { processed: number; remaining: number } | null;
  onRefresh?: () => void;
  onBuild?: (model: ModelRef) => void;
}

/**
 * Book Cache — build and monitor the manuscript's AI digest. The cache is a
 * per-scene summary stamped with the prose it was made from; AI analysis (Ask
 * the Book, Smart Context) reads it instead of re-reading every scene each time.
 * Building is incremental (only changed/uncached scenes are re-summarized).
 *
 * The actual build runs up in the workspace, not here, so it keeps going and
 * keeps the sidebar indicator spinning even if you leave this page. This panel
 * just shows status and kicks it off.
 */
export function BookCachePanel({ projectId, status, building, progress, onRefresh, onBuild }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const [reindexing, setReindexing] = useState(false);

  // Make sure the coverage shown is current when the page opens.
  useEffect(() => { onRefresh?.(); }, [onRefresh]);

  const build = async () => {
    const choice = await pickModel({ task: "general-chat" });
    if (!choice) return;
    onBuild?.(choice);
  };

  // Deterministic rebuild of the name/mention index (who appears where). No AI.
  const reindex = async () => {
    setReindexing(true);
    try {
      await api.reindex(projectId);
      toast.success("Search & mention index rebuilt.");
      onRefresh?.();
    } catch {
      toast.error("Reindex failed.");
    } finally {
      setReindexing(false);
    }
  };

  const needsWork = !!status && status.state === "stale";
  const pct = status && status.total > 0 ? Math.round((status.fresh / status.total) * 100) : 0;
  // Distinguish "content changed since caching" (scenes stale/missing) from
  // "summaries not built yet" (scenes fresh, but chapter/book summaries pending) —
  // so 100% scene coverage never reads as a misleading "Out of date".
  const sceneWork = status ? status.stale + status.missing : 0;
  const summaryWork = status ? status.chapters.stale + status.chapters.missing + (status.total > 0 && !status.bookFresh ? 1 : 0) : 0;
  const stateLabel =
    !status || status.state === "empty" ? "Nothing written yet"
      : sceneWork > 0 ? "Out of date"
        : summaryWork > 0 ? "Summaries not built"
          : "Up to date";
  const stateClass =
    !status || status.state === "empty" ? "cache-state--empty"
      : sceneWork > 0 ? "cache-state--stale"
        : summaryWork > 0 ? "cache-state--pending"
          : "cache-state--fresh";

  const buttonLabel = building
    ? "Caching…"
    : status && status.fresh === 0 && status.total > 0 ? "Cache book"
      : sceneWork > 0 ? "Update cache"
        : summaryWork > 0 ? "Build summaries"
          : "Cache up to date";

  return (
    <div className="intel-scroll">
      <section className="card insight-card">
        <div className="insight-card-head">
          <Icon name="history" size={16} style={{ color: "var(--accent)" }} />
          <h3>Book Cache</h3>
          {status && <><span className="spacer" /><span className={`cache-state ${stateClass}`}>{building ? "Refreshing…" : stateLabel}</span></>}
        </div>
        <p className="faint" style={{ fontSize: 12, margin: "0 0 12px" }}>
          A per-scene AI digest of everything written so far. Analysis like Ask the Book reads the cache instead of re-reading your whole manuscript, so it's faster and cheaper. When you add or change prose, the cache goes out of date — rebuild it (only changed scenes are re-done). You can leave this page while it builds; it keeps going.
        </p>

        {status && status.total > 0 && (
          <>
            <div className="cache-bar" title={`${status.fresh} of ${status.total} scenes cached`}>
              <div className="cache-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="cache-stats">
              <div className="cache-stat"><span className="cache-stat-n">{status.fresh}</span><span className="faint">cached</span></div>
              <div className="cache-stat"><span className="cache-stat-n">{status.stale}</span><span className="faint">out of date</span></div>
              <div className="cache-stat"><span className="cache-stat-n">{status.missing}</span><span className="faint">not cached</span></div>
              <div className="cache-stat"><span className="cache-stat-n">{pct}%</span><span className="faint">coverage</span></div>
            </div>
            <div className="faint" style={{ fontSize: 11, marginTop: 8 }}>
              {formatNumber(status.wordsCached)} of {formatNumber(status.wordsTotal)} written words covered.
            </div>
            <div className="faint" style={{ fontSize: 11, marginTop: 4 }}>
              Chapter summaries: {status.chapters.fresh}/{status.chapters.total} · Book overview: {status.bookFresh ? "ready" : "needs rebuild"}
            </div>
          </>
        )}

        {status && status.total === 0 && (
          <p className="faint" style={{ fontSize: 13 }}>There's no written prose to cache yet. Start writing scenes and come back.</p>
        )}

        <div className="row gap-2" style={{ marginTop: 14, alignItems: "center" }}>
          <button className="btn btn-sm btn-primary" onClick={() => void build()} disabled={building || !status || status.total === 0 || !needsWork}>
            <Icon name={building ? "clock" : "history"} size={14} className={building ? "spin" : ""} /> {buttonLabel}
          </button>
          <button className="btn btn-sm btn-ghost" onClick={() => void reindex()} disabled={reindexing || building}>
            <Icon name={reindexing ? "clock" : "redo"} size={14} className={reindexing ? "spin" : ""} /> Rebuild index
          </button>
          {building && progress && (
            <span className="faint" style={{ fontSize: 12 }}>
              {progress.processed} done{progress.remaining > 0 ? `, ${progress.remaining} to go` : ""}
            </span>
          )}
        </div>
        <p className="faint" style={{ fontSize: 11, marginTop: 8 }}>
          "Update cache" rebuilds only what changed (scene digests → chapter summaries → book overview). "Rebuild index" refreshes who/what appears in each scene — run it after big edits.
        </p>
      </section>
    </div>
  );
}
